import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { CreditCard, Wallet } from 'lucide-react';
import { api, type MembershipOrderPayResp, type MembershipPlanResp } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { mountAirwallexDropIn } from '@/lib/airwallex';

type PayType = 31 | 100;

const AIRWALLEX_CONTAINER_ID = 'airwallex-membership-payment-element';

type OrderTarget = {
  mode: 'create' | 'continue';
  planId?: string | number;
  orderNo?: string | number;
  planName: string;
  planPrice: number;
  originalPrice: number;
  billingCycle?: string;
  durationDays?: number;
};

interface Props {
  target: OrderTarget;
  onClose: () => void;
  onSuccess: (payload: { orderNo: string; memberEndTime?: string }) => void;
}

function buildTargetFromPlan(plan: MembershipPlanResp): OrderTarget {
  return {
    mode: 'create',
    planId: plan.id,
    planName: plan.planName || '會員訂閱',
    planPrice: Number(plan.planPrice ?? 0),
    originalPrice: Number(plan.originalPrice ?? plan.planPrice ?? 0),
    billingCycle: plan.billingCycle,
    durationDays: plan.durationDays
  };
}

export function toMembershipPaymentTarget(plan: MembershipPlanResp) {
  return buildTargetFromPlan(plan);
}

const MembershipPaymentModal = ({ target, onClose, onSuccess }: Props) => {
  const [availableAmount, setAvailableAmount] = useState(0);
  const [payType, setPayType] = useState<PayType>(31);
  const [remark, setRemark] = useState('');
  const [loading, setLoading] = useState(true);
  const [dropInReady, setDropInReady] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [pendingOrderNo, setPendingOrderNo] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, saveRedirect, refreshUser } = useAuth();
  const dropInRef = useRef<{ destroy?: () => void } | null>(null);
  const canUseBalance = availableAmount >= target.planPrice || target.planPrice === 0;

  const payOptions = useMemo(() => {
    const options: Array<{ code: PayType; label: string; desc: string; icon: 'balance' | 'airwallex' }> = [];
    if (canUseBalance) {
      options.push({ code: 100, label: '餘額支付', desc: '使用帳戶餘額', icon: 'balance' });
    }
    if (target.planPrice > 0) {
      options.push({ code: 31, label: 'Airwallex', desc: '信用卡、Google Pay', icon: 'airwallex' });
    }
    return options;
  }, [canUseBalance, target.planPrice]);

  const requestOrder = async (nextPayType: PayType): Promise<MembershipOrderPayResp> => {
    if (pendingOrderNo) {
      return api.continueMembershipOrderPay({
        orderNo: pendingOrderNo,
        payType: nextPayType
      });
    }
    if (target.mode === 'continue') {
      return api.continueMembershipOrderPay({
        orderNo: target.orderNo,
        payType: nextPayType
      });
    }
    return api.createMembershipOrder({
      planId: target.planId,
      payType: nextPayType,
      remarkCus: remark
    });
  };

  const triggerSuccessClose = (message = '付款成功') => {
    setShowSuccess(true);
    onClose();
    setError('');
  };

  const handleSuccess = async (resp: MembershipOrderPayResp) => {
    await refreshUser();
    onSuccess({
      orderNo: String(resp.orderNo),
      memberEndTime: resp.memberEndTime
    });
    triggerSuccessClose();
  };

  const confirmMembershipOrderPaid = async (orderNo?: string | number) => {
    if (!orderNo) {
      return;
    }
    await new Promise((resolve) => window.setTimeout(resolve, 1500));
    const page = await api.getMyMembershipOrders({ pageCurrent: 1, pageSize: 20 });
    const current = page.list.find((item) => String(item.orderNo) === String(orderNo));
    if (current && Number(current.orderStatus) !== 2) {
      throw new Error('membership order not paid yet');
    }
  };

  const initAirwallex = async () => {
    setLoading(true);
    setError('');
    setDropInReady(false);
    dropInRef.current?.destroy?.();
    try {
      const resp = await requestOrder(31);
      if (resp.orderStatus === 2) {
        await handleSuccess(resp);
        setLoading(false);
        return;
      }
      const instance = await mountAirwallexDropIn({
        containerId: AIRWALLEX_CONTAINER_ID,
        payMessage: resp.payMessage,
        locale: 'zh-HK',
        onReady: () => {
          setDropInReady(true);
          setLoading(false);
        },
        onCancel: () => setLoading(false),
        onError: () => {
          setLoading(false);
          setError('支付初始化失敗，請稍後再試');
        },
        onSuccess: () => {
          void (async () => {
            setLoading(true);
            try {
              await confirmMembershipOrderPaid(resp.orderNo);
            } catch {
              // Ignore delayed webhook status check failures and still complete.
            } finally {
              await handleSuccess(resp);
              setLoading(false);
            }
          })();
        }
      });
      dropInRef.current = instance;
    } catch (requestError) {
      setLoading(false);
      setError(requestError instanceof Error ? requestError.message : '支付初始化失敗');
    }
  };

  const submitBalancePay = async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await requestOrder(100);
      await handleSuccess(resp);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : '付款失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      dropInRef.current?.destroy?.();
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      const redirect = location.pathname + location.search;
      saveRedirect(redirect);
      onClose();
      navigate(`/login?redirect=${encodeURIComponent(redirect)}`);
      return;
    }

    let active = true;
    const isRecentOrder = (timestamp?: string) => {
      if (!timestamp) {
        return false;
      }
      const normalized = timestamp.includes('T') ? timestamp : timestamp.replace(' ', 'T');
      const createdAt = new Date(normalized);
      if (Number.isNaN(createdAt.getTime())) {
        return false;
      }
      return Date.now() - createdAt.getTime() <= 30 * 60 * 1000;
    };

    void (async () => {
      setLoading(true);
      try {
        const user = await api.getCurrentUser();
        if (!active) {
          return;
        }
        const balance = Number(user.availableAmount ?? 0);
        setAvailableAmount(balance);
        const nextPayType: PayType = balance >= target.planPrice || target.planPrice === 0 ? 100 : 31;

        if (target.mode === 'create' && target.planId && target.planPrice > 0) {
          const pending = await api.getMyMembershipOrders({ orderStatus: 1, pageCurrent: 1, pageSize: 5 });
          const pendingOrder = pending.list
            .filter((item) => String(item.planId) === String(target.planId))
            .filter((item) => isRecentOrder(item.gmtCreate))
            .sort((a, b) => new Date(b.gmtCreate || 0).getTime() - new Date(a.gmtCreate || 0).getTime())[0];
          if (pendingOrder?.orderNo) {
            setPendingOrderNo(String(pendingOrder.orderNo));
            setPayType(31);
            setLoading(false);
            await initAirwallex();
            return;
          }
        }

        setPayType(nextPayType);
        setLoading(false);
        if (nextPayType === 31) {
          await initAirwallex();
        }
      } catch (requestError) {
        if (active) {
          setLoading(false);
          setError(requestError instanceof Error ? requestError.message : '初始化付款失敗');
        }
      }
    })();

    return () => {
      active = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectPay = (nextPayType: PayType) => {
    if (nextPayType === payType || loading) {
      return;
    }
    setPayType(nextPayType);
    setError('');
    if (nextPayType === 31) {
      void initAirwallex();
      return;
    }
    dropInRef.current?.destroy?.();
    setDropInReady(false);
  };

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 sm:p-6 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={showSuccess ? undefined : onClose} />
      <div className="relative z-10 w-full max-w-[920px] max-h-[calc(100vh-1.5rem)] overflow-hidden rounded-[30px] border border-white/70 bg-white shadow-[0_36px_120px_-36px_rgba(15,23,42,0.5)] sm:max-h-[calc(100vh-3rem)]">
        {showSuccess ? (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/90">
            <div className="flex flex-col items-center gap-3 rounded-3xl border border-emerald-100 bg-emerald-50 px-10 py-8 text-center shadow-lg">
              <i className="ri-checkbox-circle-line text-4xl text-emerald-500" />
              <p className="text-base font-bold text-emerald-600">付款成功</p>
              <p className="text-xs text-emerald-500/80">正在關閉視窗...</p>
            </div>
          </div>
        ) : null}
        <div className="flex max-h-[calc(100vh-1.5rem)] flex-col lg:flex-row sm:max-h-[calc(100vh-3rem)]">
          <aside className="flex w-full flex-col border-b border-slate-100 bg-gradient-to-b from-slate-50 to-slate-100/90 p-6 lg:w-[280px] lg:border-b-0 lg:border-r">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">會員方案</p>
              <h3 className="mt-2 text-2xl font-black text-slate-900">{target.planName}</h3>
              <p className="mt-2 text-sm text-slate-500">{target.billingCycle || `${target.durationDays || 30} 天有效期`}</p>
            </div>

            <div className="mt-6 rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-end gap-2">
                <span className="text-4xl font-black text-slate-900">${target.planPrice}</span>
                {target.originalPrice > target.planPrice ? (
                  <span className="mb-1 text-sm text-slate-300 line-through">${target.originalPrice}</span>
                ) : null}
              </div>
              <div className="mt-3 rounded-xl bg-violet-50 px-3 py-2 text-xs font-semibold text-violet-600">
                購買後會立即更新會員身份與有效期
              </div>
            </div>

            <nav className="mt-6 space-y-2.5">
              {payOptions.map((option) => (
                <button
                  key={option.code}
                  onClick={() => handleSelectPay(option.code)}
                  disabled={loading}
                  className={`flex w-full items-center gap-3 rounded-2xl border px-4 py-4 text-left transition-all ${
                    payType === option.code
                      ? 'border-[#0d9488] bg-[#0d9488] text-white shadow-[0_18px_30px_-24px_rgba(13,148,136,0.8)]'
                      : 'border-slate-200 bg-white text-slate-700 hover:border-[#0d9488]/40 hover:shadow-sm'
                  } disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${payType === option.code ? 'bg-white/15' : 'bg-slate-100'}`}>
                    {option.icon === 'balance'
                      ? <Wallet className={`h-5 w-5 ${payType === option.code ? 'text-white' : 'text-slate-500'}`} />
                      : <CreditCard className={`h-5 w-5 ${payType === option.code ? 'text-white' : 'text-slate-500'}`} />}
                  </div>
                  <div>
                    <div className="text-sm font-bold">{option.label}</div>
                    <div className={`text-[11px] ${payType === option.code ? 'text-white/80' : 'text-slate-400'}`}>{option.desc}</div>
                  </div>
                </button>
              ))}
            </nav>

            <div className="mt-auto rounded-[24px] border border-white/80 bg-white/85 p-4 shadow-sm backdrop-blur">
              <div className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5">
                <span className="text-[11px] font-medium text-slate-400">目前餘額</span>
                <span className="text-sm font-bold text-slate-700">${availableAmount}</span>
              </div>
            </div>
          </aside>

          <main className="flex min-w-0 flex-1 flex-col p-6 lg:p-8">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-black text-slate-900">會員付款</h2>
                <p className="mt-1 text-sm text-slate-400">完成付款後，會員權益會立即生效</p>
              </div>
              <button
                onClick={onClose}
                disabled={showSuccess}
                className="flex h-10 w-10 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <div className="mt-3 min-w-0 flex-1 overflow-y-auto pr-1">
              {payType === 31 ? (
                <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 lg:p-5">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-bold text-slate-900">Airwallex 結帳</p>
                      <p className="text-xs text-slate-400">信用卡、Google Pay</p>
                    </div>
                    {!dropInReady ? (
                      <div className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[11px] text-slate-400 shadow-sm">
                        <i className="ri-loader-4-line animate-spin" />
                        載入中
                      </div>
                    ) : null}
                  </div>
                  <div className="min-h-[340px] w-full overflow-hidden rounded-[20px] bg-white p-3 shadow-[inset_0_0_0_1px_rgba(226,232,240,0.8)]">
                    <div id={AIRWALLEX_CONTAINER_ID} className="w-full max-w-full" />
                  </div>
                </div>
              ) : (
                <div className="rounded-[24px] border border-emerald-100 bg-emerald-50/80 p-6">
                  <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-6 text-center">
                    <div className="text-[13px] font-bold text-[#0d9488]">可用餘額</div>
                    <div className="text-4xl font-black text-slate-900">${availableAmount}</div>
                  </div>

                  <div className="mt-6">
                    <p className="mb-2 text-sm font-bold text-slate-600">訂單備註</p>
                    <textarea
                      value={remark}
                      onChange={(event) => setRemark(event.target.value)}
                      rows={3}
                      placeholder="可填寫報銷資訊或備註"
                      className="w-full resize-none rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700 outline-none transition-colors focus:border-slate-300"
                    />
                  </div>
                </div>
              )}
            </div>

            {error ? (
              <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                {error}
              </div>
            ) : null}

            <div className="mt-6 flex items-center justify-end gap-3 border-t border-slate-100 pt-5">
              <button onClick={onClose} className="rounded-xl px-4 py-2 text-sm text-slate-400 transition-colors hover:text-slate-700">
                關閉
              </button>
              {payType === 100 ? (
                <button
                  onClick={() => void submitBalancePay()}
                  disabled={loading}
                  className="rounded-xl bg-[#0d9488] px-8 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#0f766e] disabled:opacity-60"
                >
                  {loading ? '處理中...' : '確認付款'}
                </button>
              ) : null}
            </div>
          </main>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MembershipPaymentModal;
